import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, PLAYER_COLORS } from '../config/Constants';
import { CLASSES } from '../data/Classes';
import { TorneoConfig } from './MenuScene';
import { PlayerSelection } from './SelectionScene';
import { TorneoSystem, MatchResult, MatchData } from '../systems/TorneoSystem';
import { createEscapeMenu } from '../utils/EscapeMenu';

export class TorneoResultsScene extends Phaser.Scene {
  private config!: TorneoConfig;
  private selections!: PlayerSelection[];
  private results!: MatchResult[];
  private winnerIdx!: number;
  private torneoSystem!: TorneoSystem;
  private currentMatchData!: MatchData;
  private isTorneo: boolean = false;

  constructor() {
    super({ key: 'TorneoResultsScene' });
  }

  init(data: {
    config: TorneoConfig;
    selections: PlayerSelection[];
    arenaId: string;
    results: MatchResult[];
    winnerIdx: number;
    torneoSystem?: TorneoSystem;
    currentMatchData?: MatchData;
  }): void {
    this.config = data.config;
    this.selections = data.selections;
    this.results = data.results;
    this.winnerIdx = data.winnerIdx;
    this.isTorneo = data.torneoSystem instanceof TorneoSystem;
    if (data.torneoSystem) this.torneoSystem = data.torneoSystem;
    if (data.currentMatchData) this.currentMatchData = data.currentMatchData;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#080818');

    // Registrar resultado en el sistema de torneo
    if (this.isTorneo && this.torneoSystem && this.currentMatchData) {
      this.torneoSystem.registerMatchResult(this.currentMatchData, this.results);
    }

    // Escape menú
    createEscapeMenu(this,
      () => {},
      () => this.scene.start('TitleScene')
    );

    // Barra superior
    const titleBar = this.add.graphics();
    titleBar.fillStyle(0x1a1a3e, 0.8);
    titleBar.fillRect(0, 0, GAME_WIDTH, 70);

    const torneoOver = this.isTorneo && this.torneoSystem?.isTournamentOver();

    // Mostrar la fase actual (la de la match que acaba de terminar)
    let roundName = '📊 RESULTADOS';
    if (this.isTorneo && this.torneoSystem && this.currentMatchData) {
      roundName = this.torneoSystem.getRoundName(this.currentMatchData.round);
    }
    const title = torneoOver ? '🏆 ¡CAMPEÓN DEL TORNEO! 🏆' : roundName;

    this.add.text(GAME_WIDTH / 2, 35, title, {
      fontSize: '28px', color: '#f1c40f',
      fontFamily: 'Arial Black, Impact, sans-serif',
    }).setOrigin(0.5);

    // Si es torneo, mostrar bracket
    if (this.isTorneo && this.torneoSystem) {
      this.drawBracket();
    }

    // Podio
    this.drawPodium();

    // Botones según estado del torneo
    if (torneoOver) {
      // Torneo terminado: mostrar campeón
      this.showChampion();

      this.createButton(GAME_WIDTH / 2 - 140, GAME_HEIGHT - 50, '🏆 NUEVO TORNEO', () => {
        this.scene.start('MenuScene', { modo: 'torneo' });
      });
      this.createButton(GAME_WIDTH / 2 + 140, GAME_HEIGHT - 50, '🏠 MENÚ PRINCIPAL', () => {
        this.scene.start('TitleScene');
      });
    } else if (this.isTorneo) {
      // Siguiente ronda
      const nextMatch = this.torneoSystem!.getNextMatch();
      if (nextMatch) {
        const roundName = this.torneoSystem!.getRoundName(nextMatch.round);
        const participants = nextMatch.participants;
        const matchSelections = participants.map((idx) => this.selections[idx]);

        // Mostrar fase actual y siguiente con estilo
        const phaseText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT * 0.42, roundName, {
          fontSize: '32px', color: '#f1c40f', fontFamily: 'Arial Black',
          stroke: '#000000', strokeThickness: 4,
        }).setOrigin(0.5);

        // Participantes de la siguiente ronda
        const namesStr = participants.map((idx) => `J${idx + 1}`).join('  vs  ');
        this.add.text(GAME_WIDTH / 2, GAME_HEIGHT * 0.48, namesStr, {
          fontSize: '20px', color: '#ffffff',
        }).setOrigin(0.5);

        // Botón para continuar
        this.createButton(GAME_WIDTH / 2, GAME_HEIGHT - 50, `⏭ ${roundName}`, () => {
          this.scene.start('ArenaSelectScene', {
            config: this.config,
            selections: this.selections,
            matchSelections,
            torneoSystem: this.torneoSystem,
            currentMatchData: nextMatch,
          });
        });
      }
    } else {
      this.createButton(GAME_WIDTH / 2 - 140, GAME_HEIGHT - 50, '🔄 OTRA PARTIDA', () => {
        this.scene.start('MenuScene', { modo: this.config.modo === 'individual' ? 'rapida' : 'torneo' });
      });
      this.createButton(GAME_WIDTH / 2 + 140, GAME_HEIGHT - 50, '🏠 MENÚ PRINCIPAL', () => {
        this.scene.start('TitleScene');
      });
    }
  }

  private drawBracket(): void {
    if (!this.torneoSystem) return;

    const g = this.add.graphics();
    const startX = 30;
    const startY = 85;
    const roundW = 180;
    const cardH = 28;
    const gap = 4;

    this.torneoSystem.rounds.forEach((round, ri) => {
      const rx = startX + ri * roundW;

      // Título de ronda
      this.add.text(rx + roundW / 2, startY, this.torneoSystem!.getRoundName(ri), {
        fontSize: '11px', color: '#f1c40f', fontStyle: 'bold',
      }).setOrigin(0.5, 0);

      round.forEach((match, mi) => {
        const my = startY + 22 + mi * (cardH * match.participants.length + gap * 2);

        match.participants.forEach((pIdx, pi) => {
          const y = my + pi * cardH;
          const sel = this.selections[pIdx];
          const isWinner = match.winnerIndex === pIdx;
          const isEliminated = match.played && !isWinner;
          const bgColor = isWinner ? 0x1a3a1a : isEliminated ? 0x3a1a1a : 0x1a1a2e;

          g.fillStyle(bgColor, 0.8);
          g.fillRoundedRect(rx, y, roundW - 10, cardH - 2, 4);
          g.lineStyle(1, isWinner ? 0x2ecc71 : 0x7f8c8d, isWinner ? 1 : 0.4);
          g.strokeRoundedRect(rx, y, roundW - 10, cardH - 2, 4);

          const icon = isWinner ? '✅' : isEliminated ? '💀' : '❓';
          this.add.text(rx + 5, y + cardH / 2 - 1, `${icon} J${pIdx + 1}`, {
            fontSize: '10px', color: '#ffffff',
          }).setOrigin(0, 0.5);

          const cls = CLASSES.find((c) => c.id === sel.className);
          if (cls) {
            this.add.text(rx + 45, y + cardH / 2 - 1, cls.name.substring(0, 8), {
              fontSize: '8px', color: '#bdc3c7',
            }).setOrigin(0, 0.5);
          }
        });
      });
    });
  }

  private showChampion(): void {
    if (!this.torneoSystem) return;
    const champion = this.torneoSystem.getChampion();
    if (!champion) return;

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT * 0.38;

    // Corona
    this.add.text(cx, cy, '👑', {
      fontSize: '64px',
    }).setOrigin(0.5);

    const cls = CLASSES.find((c) => c.id === champion.selection.className);
    const color = PLAYER_COLORS[champion.playerIndex % PLAYER_COLORS.length];

    // Peonza decorativa
    const champG = this.add.graphics();
    champG.fillStyle(color, 0.8);
    champG.fillCircle(cx, cy + 80, 40);
    champG.fillStyle(Phaser.Display.Color.ValueToColor(color).darken(40).color, 0.8);
    champG.fillCircle(cx, cy + 80, 25);
    champG.fillStyle(0xffffff, 0.5);
    champG.fillCircle(cx, cy + 80, 10);

    this.add.text(cx, cy + 130, `¡JUGADOR ${champion.playerIndex + 1} CAMPEÓN!`, {
      fontSize: '36px', color: '#f1c40f', fontFamily: 'Arial Black',
      stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0.5);

    this.add.text(cx, cy + 165, `Clase: ${cls?.name || champion.selection.className}`, {
      fontSize: '20px', color: '#ffffff',
    }).setOrigin(0.5);
  }

  private drawPodium(): void {
    const sortedResults = [...this.results];
    if (this.config.modoJuego === 'dopamina') {
      sortedResults.sort((a, b) => b.points - a.points);
    } else {
      sortedResults.sort((a, b) => {
        if (a.status === 'alive' && b.status !== 'alive') return -1;
        if (a.status !== 'alive' && b.status === 'alive') return 1;
        return b.hp - a.hp;
      });
    }

    const podiumX = this.isTorneo ? GAME_WIDTH * 0.65 : GAME_WIDTH / 2;
    const startY = this.isTorneo ? 100 : 120;
    const cardH = 50;
    const medalEmojis = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣', '6️⃣'];

    sortedResults.forEach((r, i) => {
      const y = startY + i * (cardH + 6);
      const classDef = CLASSES.find((c) => c.id === r.className);
      const className = classDef?.name || r.className;
      const medal = medalEmojis[i] || `${i + 1}.`;
      const statusStr = r.status === 'alive' ? '✅' : '💀';
      const statInfo = this.config.modoJuego === 'dopamina'
        ? `${r.points} pts`
        : `❤️ ${r.hp} HP`;

      const bgColor = i === 0 ? 0x1a3a1a : i === 1 ? 0x2a2a1a : i === 2 ? 0x3a1a1a : 0x1a1a2a;
      const w = this.isTorneo ? 420 : 700;
      this.add.rectangle(podiumX, y, w, cardH, bgColor, 0.8)
        .setStrokeStyle(1, 0x7f8c8d);

      this.add.text(podiumX - w / 2 + 15, y, `${medal} J${r.playerIndex + 1}`, {
        fontSize: '17px', color: '#ffffff',
      }).setOrigin(0, 0.5);

      this.add.text(podiumX - w / 2 + 100, y, className, {
        fontSize: '15px', color: '#f1c40f',
      }).setOrigin(0, 0.5);

      this.add.text(podiumX - w / 2 + 230, y, r.specialName ? `✨` : '', {
        fontSize: '13px', color: '#9b59b6',
      }).setOrigin(0, 0.5);

      this.add.text(podiumX + 80, y, statInfo, {
        fontSize: '15px', color: '#2ecc71',
      }).setOrigin(0, 0.5);

      this.add.text(podiumX + 160, y, statusStr, {
        fontSize: '17px',
      }).setOrigin(0, 0.5);
    });

    // Título del podio
    this.add.text(podiumX, startY - 25, 'PODIO', {
      fontSize: '14px', color: '#7f8c8d', fontStyle: 'bold', letterSpacing: 3,
    }).setOrigin(0.5);
  }

  private createButton(x: number, y: number, text: string, onClick: () => void): void {
    const bg = this.add.rectangle(x, y, 260, 45, 0x2c3e50)
      .setStrokeStyle(2, 0x3498db)
      .setInteractive({ useHandCursor: true });
    this.add.text(x, y, text, {
      fontSize: '16px', color: '#ffffff',
    }).setOrigin(0.5);

    bg.on('pointerover', () => bg.setFillStyle(0x3498db));
    bg.on('pointerout', () => bg.setFillStyle(0x2c3e50));
    bg.on('pointerdown', onClick);
  }
}
